import EventEmitter from '../classes/EventEmiter'
import type {
  Icomponent,
  IContainers,
  IProcessedComponent,
  IProcessedComponentProps,
  IProcessedContainer,
  UnresolvedRecord,
} from '../interfaces'
import { RootState } from '../redux/store'
import { processActions } from '../utils/processActions'
import processData from '../utils/processData'
import { getMinAmountProgressMessage } from '../utils/minAmountProgressMessage'
import { isHidePricesEnabled } from '../utils/hidePrices'
import maskPricesForDisplay from '../utils/maskPricesForDisplay'
import { getCartCheckoutPanelProps } from '../utils/cartCheckoutPanel'
import { getOrderDetailPanelProps } from '../utils/orderDetailPanel'
import { getOrderConfirmationPanelProps } from '../utils/orderConfirmationPanel'

const MIN_AMOUNT_IMPORT_ROUTE = 'MinAmount'

type PanelPropInjector = (state: RootState) => UnresolvedRecord

/**
 * Components whose props cinnamon partly owns, and the injector for each.
 *
 * A lookup rather than a chain of `importRoute ===` ternaries because there are
 * now two. A route with no entry is left entirely to config, which is every
 * other component in the library.
 *
 * A `Map` rather than an object literal, for two reasons that both come from
 * `importRoute` being an arbitrary string out of remote JSON. `Map.get` returns
 * `PanelPropInjector | undefined`, so the guard at the call site is typed
 * honestly — an object index signature types as never-undefined under this
 * repo's `strict` (no `noUncheckedIndexedAccess`), which makes a load-bearing
 * guard read as dead code to the next person. And a `Map`'s entries are not
 * looked up on a prototype chain, so an `importRoute` of `'constructor'`
 * finds nothing — where an object literal resolves it to
 * `Object.prototype.constructor`, and `Object(state)` returns `state`, which
 * would spread the entire store into one component's props.
 */
const PANEL_PROP_INJECTORS = new Map<string, PanelPropInjector>([
  ['CartCheckoutPanel', getCartCheckoutPanelProps],
  ['OrderDetailPanel', getOrderDetailPanelProps],
  ['OrderConfirmationPanel', getOrderConfirmationPanelProps],
])

const resolveProps = (
  data: UnresolvedRecord,
  state: RootState,
  preserveTotal = false,
) => {
  const resolved = processData(data, { state })
  if (!isHidePricesEnabled(state)) return resolved
  return maskPricesForDisplay(resolved, preserveTotal)
}

const getCinnamonProps = <T = unknown>(
  props: UnresolvedRecord,
  state: RootState,
  eventEmitter: EventEmitter,
  preserveTotal = false,
): IProcessedComponentProps<T> => {
  const cinnamonProps = resolveProps(props, state, preserveTotal)
  return processActions(cinnamonProps, eventEmitter)
}

const mapComponent =
  (state: RootState, eventEmitter: EventEmitter) =>
  ({ props = {}, ...rest }: Icomponent): IProcessedComponent => {
    const isMinAmount = rest.importRoute === MIN_AMOUNT_IMPORT_ROUTE
    /*
     * The props the order panels own rather than the config author. Each
     * injector documents its own list — between them the order in the panel's
     * shape, the derived panel status, the decline reason, the external ref
     * status and whether this shopper sees prices. None is expressible as a
     * `{{variable}}`: they are computed from state, or are a shape the DSL
     * cannot build, so they are merged in ahead of resolution.
     *
     * Ahead of it, not after, so the injected order goes through
     * `maskPricesForDisplay` with everything else. Injecting afterwards, the
     * way the MinAmount message below is injected, would hand a hide-prices
     * shopper the real totals; that one is safe there only because it is a
     * string with no money in it.
     *
     * Config still owns every label, the state copy, the currency, the status
     * map, `fieldsToDisplay`, the slots and the actions.
     */
    // No `importRoute &&` guard: the field is declared non-optional `string`,
    // so the check was one the type forbids reaching, and `Map.get(undefined)`
    // returns `undefined` anyway.
    const injectPanelProps = PANEL_PROP_INJECTORS.get(rest.importRoute)
    const propsToResolve = injectPanelProps
      ? { ...props, ...injectPanelProps(state) }
      : props
    const resolvedProps = getCinnamonProps(
      propsToResolve,
      state,
      eventEmitter,
      isMinAmount,
    )

    if (isMinAmount) {
      const message = getMinAmountProgressMessage(state, resolvedProps)
      if (message !== undefined) {
        resolvedProps.message = message
      }
    }

    return { ...rest, props: resolvedProps }
  }

const processDataValidated = (
  data: UnresolvedRecord | undefined = {},
  state: RootState,
) => resolveProps(data, state)

export const getCinnamonPropsContainer = (
  { header, body, footer, modals, uid }: IContainers,
  state: RootState,
  eventEmitter: EventEmitter,
): IProcessedContainer => {
  const toProcessedComponent = mapComponent(state, eventEmitter)
  return {
    uid,
    modals,
    header: {
      props: processDataValidated(header?.props, state),
      components:
        header?.components.map<IProcessedComponent>(toProcessedComponent) || [],
    },
    body: {
      props: processDataValidated(body?.props, state),
      components:
        body?.components.map<IProcessedComponent>(toProcessedComponent) || [],
    },
    footer: {
      props: processDataValidated(footer?.props, state),
      components:
        footer?.components.map<IProcessedComponent>(toProcessedComponent) || [],
    },
  }
}

export default getCinnamonProps
